<template>
    <div>
        <w-app
            :title="$t('module.cnas_agency_tools')"
            show-total
            :total="cnasAgencies.length"
        >
            <template v-slot:leftToolbar>
                <ImportModel file-name="CnasAgence.xlsx"/>
            </template>
            <template v-slot:rightToolbar>
                <w-btn
                    color="teal"
                    icon="mdi-import"
                    :text="$t('common.import')"
                    @press="dialog = true"
                />
                <w-btn
                    v-if="securityStore.authorities['UPDATE_IMPORT_PARAM']"
                    color="red"
                    icon="mdi-table-check"
                    :text="$t('common.apply')"
                    :loading="loading"
                    @press="apply"
                />

            </template>

            <template v-slot:content>
                <v-container fluid>
                    <v-row dense>
                        <v-col cols="4">
                            <w-text-field v-model="search" :label="$t('common.search')"/>
                        </v-col>
                    </v-row>
                </v-container>
                <v-skeleton-loader
                    v-show="loading"
                    ref="skeleton"
                    :type="'table'"
                    :class="`mx-auto elevation-${tableElevation}`"
                ></v-skeleton-loader>
                <v-data-table
                    v-show="!loading"
                    :headers="headers"
                    :search="search"
                    :items="cnasAgencies"
                    :loading="loading"
                    dense
                    :class="`elevation-${tableElevation}`"
                    :page.sync="page"
                    :items-per-page="itemsPerPage"
                    hide-default-footer
                    @page-count="pageCount = $event"
                >
                    <template v-slot:footer="{}">
                        <br/>
                        <div class="text-center">
                            <v-row justify="center">
                                <v-col md="6">
                                    <v-pagination
                                        v-model="page"
                                        :length="pageCount"
                                    ></v-pagination>
                                </v-col>
                                <v-col md="2">
                                    <w-enum-select
                                        v-model="itemsPerPage"
                                        :items="paginationListLength"
                                        :label="$t('common.items_per_page')"
                                    />
                                </v-col>
                            </v-row>
                        </div>
                    </template>
                </v-data-table>
            </template>
        </w-app>
        <v-dialog
            v-model="dialog"
            persistent
            max-width="700"
        >
            <v-card>
                <v-toolbar flat height="40" dark color="teal">
                    <v-toolbar-title>{{ $t('common.import') }}</v-toolbar-title>
                </v-toolbar>
                <v-card-text>
                    <v-container fluid>
                        <v-row dense>
                            <v-col cols="12">
                                <strong>{{ $t('common.file') }} *</strong>
                                <w-file-input
                                    v-model="file"
                                    :rules="[mxRules.required]"
                                />
                            </v-col>
                        </v-row>
                    </v-container>
                </v-card-text>
                <v-divider/>
                <v-card-actions>
                    <v-spacer/>
                    <v-btn
                        depressed
                        outlined
                        color="teal"
                        @click="dialog = false"
                    >
                        {{ $t('common.close') }}
                        <v-icon right>mdi-close</v-icon>
                    </v-btn>
                    <v-btn
                        depressed
                        class="white--text"
                        color="teal"
                        :loading="loading"
                        @click="upload"
                    >
                        {{ $t('common.import') }}
                        <v-icon right>mdi-import</v-icon>
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import {ref} from "vue";
import useTools from "@/use/Tools";
import useValidation from "@/use/Validation";
import useSharedData from "@/use/SharedData";
import axios from "axios";
import ImportModel from "@/views/Tools/Components/ImportModel.vue";
import {useSecurityStore} from "@/stores/Security";

export default {
    name: "CnasAgencyTools",
    components: {ImportModel},
    setup() {
        const {mxRules} = useValidation();
        const {$swal, $i18n} = useTools();
        const securityStore = useSecurityStore();
        const {
            dialog,
            loading,
            tableElevation,
            page,
            pageCount,
            itemsPerPage,
            paginationListLength,
        } = useSharedData();
        dialog.value = false;

        const file = ref(null);
        const cnasAgencies = ref([]);
        const search = ref(null);

        const headers = [
            {
                text: $i18n.t("common.code"),
                align: "left",
                sortable: true,
                value: "code",
            },
            {
                text: $i18n.t("common.name"),
                align: "left",
                sortable: true,
                value: "name",
            },
            {
                text: $i18n.t("common.arabic_name"),
                align: "left",
                sortable: true,
                value: "arabicName",
            },
            {
                text: $i18n.t("common.membership_number"),
                align: "left",
                sortable: true,
                value: "membershipNumber",
            },
            {
                text: $i18n.t("common.accounting_code"),
                align: "left",
                sortable: true,
                value: "accountingCode",
            },
            {
                text: $i18n.t("common.agency_code"),
                align: "left",
                sortable: true,
                value: "agencyCode",
            },
        ];


        function upload() {
            loading.value = true;
            let formData = new FormData();
            formData.append('file', file.value);
            axios.post(`/tools/getCnasAgencies`, formData).then(response => {
                cnasAgencies.value = response.data;
                dialog.value = false;
            }).finally(() => {
                loading.value = false;
            });
        }

        function apply() {
            loading.value = true;
            axios.post(`/cnasAgencies/saveAll`, cnasAgencies.value).then(() => {
                $swal.fire('Très Bien', "Importation des agences terminer", 'success')
                cnasAgencies.value = [];
            }).finally(() => {
                loading.value = false;
            });
        }

        return {
            securityStore,
            mxRules,
            dialog,
            loading,
            tableElevation,
            page,
            pageCount,
            itemsPerPage,
            paginationListLength,
            headers,
            file,
            search,
            cnasAgencies,
            apply,
            upload,
        }
    }
}
</script>

<style scoped>

</style>